import "./MemberDirectory.css";
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import editIcon from "../assets/icons/edit.png";
import BackIcon from "../assets/icons/arrow.png";
import { useAuthStore } from "../Services/authStore";
import { toastSuccess, toastError, toastWarning } from "../Services/toaster";

// ─────────────────────────────────────────────────────────
//  MEMBER DIRECTORY COMPONENT
// ─────────────────────────────────────────────────────────
export default function MemberDirectory() {
  const [showForm, setShowForm] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [editingId, setEditingId] = useState(null);

  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState("All");

  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    birth_date: "",
    title: "",
    gender: "",
    nationality: "",
    additional_spoken_language: "",
    spouse_member: "",
    other_associations: "",
    phone: "",
    email: "",
    national_bailliage: "",
    prov_bailliage: "",
    reg_bailliage: "",
    sponsor1: "",
    sponsor2: "",
    membership_type: "",
    grade_id: 0,
    relevant_year: "",
    registration_date: "",
    price_category: "",
    amount: "",
    address: "",
    add_information: "",
    country: "",
    state: "",
    postal_code: "",
    city: "",
    rank_id: 0,
    diet_pref: "",
    is_active: 1,
  });

  const [formError, setFormError] = useState("");
  const [formLoading, setFormLoading] = useState(false);

  const token = useAuthStore((state) => state.token);
  const API_URL = process.env.REACT_APP_API_URL;

  // ─── Dropdown Options ─────────────────────────────────
  const titleOptions = [
    "Mr.",
    "Mrs.",
    "Ms.",
    "Miss",
    "Dr.",
    "Prof.",
    "Lady",
    "Chef",
    "Count",
    "Dato",
  ];

  const genderOptions = ["Male", "Female", "Other"];

  const membershipTypeOptions = ["Amateur", "Professional"];

  const dietPrefOptions = [
    "No restrictions",
    "Vegetarian",
    "Vegan",
    "Gluten-free",
    "Halal",
    "Kosher",
    "Pescatarian",
    "No pork",
    "No dairy",
    "No seafood",
    "Organic only",
    "Diabetic",
    "Low sodium",
  ];

  // ─── GET /members - Fetch All Members ────────────────
  const getMembers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/members`, {
        headers: {
          Authorization: `Bearer ${token}`,
          accept: "application/json",
        },
      });
      const data = response.data?.data || response.data || [];
      setMembers(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to load members:", err.response?.data);
      toastError("Failed to load members");
      setMembers([]);
    } finally {
      setLoading(false);
    }
  }, [API_URL, token]);

  useEffect(() => {
    getMembers();
  }, [getMembers]);

  // ─── GET /members/{member_id} - Fetch Single Member ─
  const getMemberById = useCallback(
    async (id) => {
      try {
        const response = await axios.get(`${API_URL}/members/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            accept: "application/json",
          },
        });
        return response.data?.data || response.data;
      } catch (err) {
        console.error("Failed to fetch member:", err.response?.data);
        throw err;
      }
    },
    [API_URL, token],
  );

  // ─── PUT /members/{member_id} - Update Member ───────
  const updateMember = async (id, memberData) => {
    try {
      const payload = {
        first_name: memberData.first_name,
        last_name: memberData.last_name,
        birth_date: memberData.birth_date,
        title: memberData.title,
        gender: memberData.gender,
        nationality: memberData.nationality,
        additional_spoken_language: memberData.additional_spoken_language,
        spouse_member: memberData.spouse_member,
        other_associations: memberData.other_associations,
        phone: memberData.phone,
        email: memberData.email,
        national_bailliage: memberData.national_bailliage,
        prov_bailliage: memberData.prov_bailliage,
        reg_bailliage: memberData.reg_bailliage,
        sponsor1: memberData.sponsor1,
        sponsor2: memberData.sponsor2,
        membership_type: memberData.membership_type,
        grade_id: parseInt(memberData.grade_id) || 0,
        relevant_year: parseInt(memberData.relevant_year) || 0,
        registration_date: memberData.registration_date,
        price_category: memberData.price_category,
        amount: parseFloat(memberData.amount) || 0,
        address: memberData.address,
        add_information: memberData.add_information,
        country: memberData.country,
        state: memberData.state,
        postal_code: memberData.postal_code,
        city: memberData.city,
        rank_id: parseInt(memberData.rank_id) || 0,
        diet_pref: memberData.diet_pref,
        is_active: memberData.is_active === 1,
      };

      const response = await axios.put(`${API_URL}/members/${id}`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          accept: "application/json",
          "Content-Type": "application/json",
        },
      });
      return response.data;
    } catch (err) {
      console.error("Update error:", err.response?.data);
      throw err;
    }
  };

  // ─── Open Edit ────────────────────────────────────────
  const openEdit = async (member) => {
    setFormError("");
    setIsEditMode(true);
    setSelectedMember(member);
    setEditingId(member.id);
    setFormLoading(true);
    setShowForm(true);

    try {
      const memberData = await getMemberById(member.id);
      setFormData({
        first_name: memberData.first_name || "",
        last_name: memberData.last_name || "",
        birth_date: memberData.birth_date || "",
        title: memberData.title || "",
        gender: memberData.gender || "",
        nationality: memberData.nationality || "",
        additional_spoken_language: memberData.additional_spoken_language || "",
        spouse_member: memberData.spouse_member || "",
        other_associations: memberData.other_associations || "",
        phone: memberData.phone || "",
        email: memberData.email || "",
        national_bailliage: memberData.national_bailliage || "",
        prov_bailliage: memberData.prov_bailliage || "",
        reg_bailliage: memberData.reg_bailliage || "",
        sponsor1: memberData.sponsor1 || "",
        sponsor2: memberData.sponsor2 || "",
        membership_type: memberData.membership_type || "",
        grade_id: memberData.grade_id || 0,
        relevant_year: memberData.relevant_year || "",
        registration_date: memberData.registration_date || "",
        price_category: memberData.price_category || "",
        amount: memberData.amount || "",
        address: memberData.address || "",
        add_information: memberData.add_information || "",
        country: memberData.country || "",
        state: memberData.state || "",
        postal_code: memberData.postal_code || "",
        city: memberData.city || "",
        rank_id: memberData.rank_id || 0,
        diet_pref: memberData.diet_pref || "",
        is_active: memberData.is_active ? 1 : 0,
      });
    } catch (err) {
      const msg = "Failed to load member details.";
      setFormError(msg);
      toastError(msg);
    } finally {
      setFormLoading(false);
    }
  };

  const closeForm = () => {
    setShowForm(false);
    setFormData({
      first_name: "",
      last_name: "",
      birth_date: "",
      title: "",
      gender: "",
      nationality: "",
      additional_spoken_language: "",
      spouse_member: "",
      other_associations: "",
      phone: "",
      email: "",
      national_bailliage: "",
      prov_bailliage: "",
      reg_bailliage: "",
      sponsor1: "",
      sponsor2: "",
      membership_type: "",
      grade_id: 0,
      relevant_year: "",
      registration_date: "",
      price_category: "",
      amount: "",
      address: "",
      add_information: "",
      country: "",
      state: "",
      postal_code: "",
      city: "",
      rank_id: 0,
      diet_pref: "",
      is_active: 1,
    });
    setFormError("");
    setSelectedMember(null);
    setEditingId(null);
    setIsEditMode(false);
  };

  const handleChange = (e) => {
    const value =
      e.target.type === "checkbox"
        ? e.target.checked
          ? 1
          : 0
        : e.target.value;
    setFormData({ ...formData, [e.target.name]: value });
  };

  // ─── Handle Submit ────────────────────────────────────
  const handleSubmit = async () => {
    setFormError("");

    if (!formData.first_name.trim()) {
      const msg = "First name is required.";
      setFormError(msg);
      toastWarning(msg);
      return;
    }
    if (!formData.last_name.trim()) {
      const msg = "Last name is required.";
      setFormError(msg);
      toastWarning(msg);
      return;
    }
    if (!formData.email.trim()) {
      const msg = "Email is required.";
      setFormError(msg);
      toastWarning(msg);
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      const msg = "Please enter a valid email address.";
      setFormError(msg);
      toastWarning(msg);
      return;
    }
    if (!formData.phone.trim()) {
      const msg = "Phone number is required.";
      setFormError(msg);
      toastWarning(msg);
      return;
    }

    setFormLoading(true);

    try {
      await updateMember(editingId, formData);
      toastSuccess("Member updated successfully.");
      closeForm();
      getMembers();
    } catch (err) {
      const detail = err.response?.data?.detail || err.response?.data?.message;
      const msg = Array.isArray(detail)
        ? detail.map((d) => `${d.loc?.join(".")} — ${d.msg}`).join("\n")
        : detail || "Failed to update member.";
      setFormError(msg);
      toastError(msg);
    } finally {
      setFormLoading(false);
    }
  };

  // ─── Filter Members ────────────────────────────────────
  const filteredMembers = members.filter((m) => {
    const matchType =
      typeFilter === "All" ||
      m.membership_type === typeFilter ||
      (typeFilter === "Active" && m.is_active) ||
      (typeFilter === "Inactive" && !m.is_active);

    const search = searchTerm.toLowerCase();
    const matchSearch =
      (m.first_name || "").toLowerCase().includes(search) ||
      (m.last_name || "").toLowerCase().includes(search) ||
      (m.email || "").toLowerCase().includes(search) ||
      (m.phone || "").includes(search) ||
      (m.registration_number || "").toLowerCase().includes(search) ||
      (m.grade || "").toLowerCase().includes(search);

    return matchType && matchSearch;
  });

  // ── Stats ──
  const totalCount = members.length;
  const activeCount = members.filter((m) => m.is_active).length;
  const amateurCount = members.filter(
    (m) => m.membership_type === "Amateur",
  ).length;
  const professionalCount = members.filter(
    (m) => m.membership_type === "Professional",
  ).length;

  // ── Format Helpers ──
  const formatDate = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // ════════════════════════════════════════════════════
  //  EDIT FORM
  // ════════════════════════════════════════════════════
  if (showForm) {
    return (
      <div className="md-form-page">
        <div className="md-form-header">
          <div className="md-form-header-left">
            <button className="md-back-btn" onClick={closeForm}>
              <img src={BackIcon} alt="Back" className="md-back-icon" />
            </button>
            <div>
              <h2 className="md-form-title">Edit Member</h2>
              <p className="md-form-subtitle">
                {selectedMember?.registration_number} ·{" "}
                {selectedMember?.first_name} {selectedMember?.last_name}
              </p>
            </div>
          </div>
        </div>

        <div className="md-form-card">
          {formLoading && (
            <p style={{ color: "#9F8E87", fontSize: 13, marginBottom: 14 }}>
              Loading...
            </p>
          )}
          {formError && (
            <p
              style={{
                color: "#b94040",
                fontSize: 13,
                marginBottom: 14,
                whiteSpace: "pre-line",
              }}
            >
              {formError}
            </p>
          )}

          <div className="md-form-grid">
            {/* Personal Info */}
            <div className="md-section-title md-col-full">
              <h3>Personal Information</h3>
            </div>

            <div className="md-form-group">
              <label>Title</label>
              <select
                name="title"
                value={formData.title}
                onChange={handleChange}
              >
                <option value="">Select Title</option>
                {titleOptions.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>

            <div className="md-form-group">
              <label>First Name *</label>
              <input
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                placeholder="Enter first name"
              />
            </div>

            <div className="md-form-group">
              <label>Last Name *</label>
              <input
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                placeholder="Enter last name"
              />
            </div>

            <div className="md-form-group">
              <label>Birth Date</label>
              <input
                name="birth_date"
                type="date"
                value={formData.birth_date}
                onChange={handleChange}
              />
            </div>

            <div className="md-form-group">
              <label>Gender</label>
              <select
                name="gender"
                value={formData.gender}
                onChange={handleChange}
              >
                <option value="">Select Gender</option>
                {genderOptions.map((g) => (
                  <option key={g} value={g}>
                    {g}
                  </option>
                ))}
              </select>
            </div>

            <div className="md-form-group">
              <label>Nationality</label>
              <input
                name="nationality"
                value={formData.nationality}
                onChange={handleChange}
                placeholder="Enter nationality"
              />
            </div>

            <div className="md-form-group">
              <label>Additional Spoken Language</label>
              <input
                name="additional_spoken_language"
                value={formData.additional_spoken_language}
                onChange={handleChange}
                placeholder="e.g. Spanish, French"
              />
            </div>

            <div className="md-form-group">
              <label>Spouse Member</label>
              <input
                name="spouse_member"
                value={formData.spouse_member}
                onChange={handleChange}
                placeholder="Yes / No"
              />
            </div>

            <div className="md-form-group md-col-full">
              <label>Other Gastronomic Associations</label>
              <textarea
                name="other_associations"
                rows="2"
                value={formData.other_associations}
                onChange={handleChange}
                placeholder="Additional associations..."
              />
            </div>

            {/* Contact Info */}
            <div className="md-section-title md-col-full">
              <h3>Contact Information</h3>
            </div>

            <div className="md-form-group">
              <label>Phone *</label>
              <input
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="+91 9876543210"
              />
            </div>

            <div className="md-form-group">
              <label>Email *</label>
              <input
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="member@example.com"
              />
            </div>

            {/* Bailliage Info */}
            <div className="md-section-title md-col-full">
              <h3>Bailliage Information</h3>
            </div>

            <div className="md-form-group">
              <label>National Bailliage</label>
              <input
                name="national_bailliage"
                value={formData.national_bailliage}
                onChange={handleChange}
                placeholder="Enter national bailliage"
              />
            </div>

            <div className="md-form-group">
              <label>Provincial Bailliage</label>
              <input
                name="prov_bailliage"
                value={formData.prov_bailliage}
                onChange={handleChange}
                placeholder="Enter provincial bailliage"
              />
            </div>

            <div className="md-form-group">
              <label>Regional Bailliage</label>
              <input
                name="reg_bailliage"
                value={formData.reg_bailliage}
                onChange={handleChange}
                placeholder="Enter regional bailliage"
              />
            </div>

            {/* Membership Info */}
            <div className="md-section-title md-col-full">
              <h3>Membership Information</h3>
            </div>

            <div className="md-form-group">
              <label>Sponsor 1</label>
              <input
                name="sponsor1"
                value={formData.sponsor1}
                onChange={handleChange}
                placeholder="Sponsor 1 name"
              />
            </div>

            <div className="md-form-group">
              <label>Sponsor 2</label>
              <input
                name="sponsor2"
                value={formData.sponsor2}
                onChange={handleChange}
                placeholder="Sponsor 2 name"
              />
            </div>

            <div className="md-form-group">
              <label>Membership Type</label>
              <select
                name="membership_type"
                value={formData.membership_type}
                onChange={handleChange}
              >
                <option value="">Select Type</option>
                {membershipTypeOptions.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>

            <div className="md-form-group">
              <label>Registration Date</label>
              <input
                name="registration_date"
                type="date"
                value={formData.registration_date}
                onChange={handleChange}
              />
            </div>

            {/* Address Info */}
            <div className="md-section-title md-col-full">
              <h3>Address Information</h3>
            </div>

            <div className="md-form-group md-col-full">
              <label>Address</label>
              <input
                name="address"
                value={formData.address}
                onChange={handleChange}
                placeholder="Street address"
              />
            </div>

            <div className="md-form-group">
              <label>Country</label>
              <input
                name="country"
                value={formData.country}
                onChange={handleChange}
                placeholder="Enter country"
              />
            </div>

            <div className="md-form-group">
              <label>State</label>
              <input
                name="state"
                value={formData.state}
                onChange={handleChange}
                placeholder="Enter state"
              />
            </div>

            <div className="md-form-group">
              <label>City</label>
              <input
                name="city"
                value={formData.city}
                onChange={handleChange}
                placeholder="Enter city"
              />
            </div>

            <div className="md-form-group">
              <label>Postal Code</label>
              <input
                name="postal_code"
                value={formData.postal_code}
                onChange={handleChange}
                placeholder="Enter postal code"
              />
            </div>

            {/* Additional Info */}
            <div className="md-section-title md-col-full">
              <h3>Additional Information</h3>
            </div>

            <div className="md-form-group md-col-full">
              <label>Additional Information</label>
              <textarea
                name="add_information"
                rows="3"
                value={formData.add_information}
                onChange={handleChange}
                placeholder="Any additional notes..."
              />
            </div>

            <div className="md-form-group md-col-full">
              <label>Dietary Preferences</label>
              <select
                name="diet_pref"
                value={formData.diet_pref}
                onChange={handleChange}
              >
                <option value="">Select Dietary Preference</option>
                {dietPrefOptions.map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </select>
            </div>

            {/* Status */}
            <div className="md-form-group">
              <label>Status</label>
              <div className="md-status-toggle">
                <label className="md-switch">
                  <input
                    type="checkbox"
                    checked={formData.is_active === 1}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        is_active: e.target.checked ? 1 : 0,
                      })
                    }
                  />
                  <span className="md-slider"></span>
                </label>
                <span className="md-status-label">
                  {formData.is_active === 1 ? "Active" : "Inactive"}
                </span>
              </div>
            </div>
          </div>

          <div className="md-form-footer">
            <button className="md-btn md-btn-secondary" onClick={closeForm}>
              Cancel
            </button>
            <button
              className="md-btn md-btn-primary"
              onClick={handleSubmit}
              disabled={formLoading}
            >
              {formLoading ? "Saving..." : "Update Member"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ════════════════════════════════════════════════════
  //  MEMBER LIST
  // ════════════════════════════════════════════════════
  return (
    <div className="md-page">
      {/* ─── Top Bar ─── */}
      <div className="md-page-header">
        {/* <div>
          <h1 className="md-page-title"> </h1>
          <p className="md-page-subtitle"></p>
        </div> */}
        <div className="md-page-actions">
          <div className="md-stats">
            <div className="md-stat-pill md-stat-total">
              <span className="md-stat-num">{totalCount}</span>
              <span className="md-stat-lbl">Total</span>
            </div>
            <div className="md-stat-pill md-stat-active">
              <span className="md-stat-num">{activeCount}</span>
              <span className="md-stat-lbl">Active</span>
            </div>
            <div className="md-stat-pill md-stat-amateur">
              <span className="md-stat-num">{amateurCount}</span>
              <span className="md-stat-lbl">Amateur</span>
            </div>
            <div className="md-stat-pill md-stat-professional">
              <span className="md-stat-num">{professionalCount}</span>
              <span className="md-stat-lbl">Professional</span>
            </div>
          </div>

          <div className="md-search-box">
            <span className="md-search-icon">🔍</span>
            <input
              type="text"
              placeholder="Search members..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* ─── Stats ─── */}

      {/* ─── Filter Tabs ─── */}
      {/* <div className="md-toolbar">
        <div className="md-filter-tabs">
          {["All", "Amateur", "Professional", "Active", "Inactive"].map(
            (tab) => (
              <button
                key={tab}
                className={`md-filter-tab ${
                  typeFilter === tab ? "md-filter-tab-active" : ""
                }`}
                onClick={() => setTypeFilter(tab)}
              >
                {tab}
              </button>
            ),
          )}
        </div>
      </div> */}

      {/* ─── Table ─── */}
      <div className="md-table-card">
        <div className="md-table-responsive">
          <table className="md-table">
            <thead>
              <tr>
                <th className="md-col-id">#</th>
                <th className="md-col-reg">Registration No.</th>
                <th className="md-col-name">Name</th>
                <th className="md-col-contact">Contact</th>
                <th className="md-col-grade">Grade / Rank</th>
                <th className="md-col-type">Type</th>
                <th className="md-col-location">Location</th>
                <th className="md-col-diet">Diet</th>
                <th className="md-col-active">Status</th>
                <th className="md-col-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="10" className="md-empty-row">
                    Loading members...
                  </td>
                </tr>
              ) : filteredMembers.length === 0 ? (
                <tr>
                  <td colSpan="10">
                    <div className="md-empty-state">
                      <div className="md-empty-icon">👥</div>
                      <div className="md-empty-title">No Members Found</div>
                      <div className="md-empty-desc">
                        {searchTerm
                          ? `No members match "${searchTerm}"`
                          : "No members registered yet"}
                      </div>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredMembers.map((m, index) => (
                  <tr key={m.id}>
                    <td className="md-col-id">{index + 1}</td>
                    <td className="md-col-reg">
                      <span className="md-reg-badge">
                        {m.registration_number || "—"}
                      </span>
                    </td>
                    <td className="md-col-name">
                      <div className="md-full-name">
                        <span className="md-title">{m.title}</span>
                        <span className="md-first">{m.first_name}</span>
                        <span className="md-last">{m.last_name}</span>
                      </div>
                      <div className="md-dob">
                        Born: {formatDate(m.birth_date)}
                      </div>
                    </td>
                    <td className="md-col-contact">
                      <div className="md-email">
                        <a href={`mailto:${m.email}`} className="md-email-link">
                          {m.email}
                        </a>
                      </div>
                      <div className="md-phone">{m.phone}</div>
                    </td>
                    <td className="md-col-grade">
                      <div className="md-grade-info">
                        {m.grade && <div className="md-grade">{m.grade}</div>}
                        {m.rank && <div className="md-rank">{m.rank}</div>}
                      </div>
                    </td>
                    <td className="md-col-type">
                      <span
                        className={`md-type-badge ${
                          m.membership_type === "Professional"
                            ? "md-type-professional"
                            : "md-type-amateur"
                        }`}
                      >
                        {m.membership_type || "—"}
                      </span>
                    </td>
                    <td className="md-col-location">
                      <div>{m.city || "—"}</div>
                      <div className="md-country">{m.country || "—"}</div>
                    </td>
                    <td className="md-col-diet">
                      {m.diet_pref ? (
                        <span className="md-diet-badge">{m.diet_pref}</span>
                      ) : (
                        <span className="md-none">—</span>
                      )}
                    </td>
                    <td className="md-col-active">
                      <span
                        className={`md-status-badge ${
                          m.is_active
                            ? "md-status-active"
                            : "md-status-inactive"
                        }`}
                      >
                        {m.is_active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="md-col-actions">
                      <div className="md-action-group">
                        <button
                          className="md-action-btn md-action-btn-edit"
                          onClick={() => openEdit(m)}
                          title="Edit"
                        >
                          <img
                            src={editIcon}
                            alt="Edit"
                            className="md-action-icon"
                          />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
